import React, { useState, useEffect, Suspense } from 'react';
import SysOwnerSideBar from './SysOwnerSidebar';
import { useNavigate, useParams } from 'react-router-dom';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import ErrorBoundary from '../../common/ErrorBoundery';
import LogoLoader from '../../common/LogoLoader';
import appService from '../../../services/ApplicationService';
import userService from '../../../services/UserService';
import uarService from '../../../services/UARService';
import CustomSpeedDial from '../../common/SpeedDial';
import Modal from '../../common/Modal';
import MultipleSelect from '../../common/MultipleSelect';
import NormalTextField from '../../common/NormalTextField';
import DynamicSnackbar from '../../common/Snackbar';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';

// Icons
import {
    RateReview as RateReviewIcon,
    FolderOpen as FolderOpenIcon,
    Add as AddIcon,
    Check as CheckIcon,
    Warning as WarningIcon,
    EditCalendar as EditCalendarIcon,
    CalendarMonth as CalendarMonthIcon,
    AccessTime as AccessTimeIcon,
    Settings as SettingsIcon,
    HourglassEmpty as HourglassEmptyIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

const globalStyles = `
    @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
    }
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slideIn {
        from { transform: translateX(-10px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
    }
    @keyframes progressAnimation {
        0% { width: 0%; }
        100% { width: 100%; }
    }
`;

const DataTable = React.lazy(() => import('../../common/DataGrid'));

const UserAccessReviewDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    // State variables
    const [appInfo, setAppInfo] = useState(null);
    const [uar, setUAR] = useState([]);
    const [uarExist, setUARExist] = useState(false);
    const [uarFile, setUARFile] = useState({});
    const [selectedFrequency, setSelectedFrequency] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // UI control state
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openSetupModal, setOpenSetupModal] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [saved, setSaved] = useState(false);

    // Get the valid start date if it exists
    const startDate = dayjs(uarFile.START_DATE);
    const isValidStartDate = startDate.isValid();

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = globalStyles;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                
                // Add a short delay for loading animation
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                // Fetch application details
                const app = await appService.fetchAppsById(id);
                if (app) {
                    setAppInfo(app);
                }

                // Fetch UAR records for this app
                const uarRecords = await uarService.fetchUARByApp(id);
                if (uarRecords && uarRecords.length > 0) {
                    setUARExist(true);
                    setOpenCreateModal(false);
                    setOpenSetupModal(false);
                    setUAR(uarRecords);
                } else {
                    // No records found
                    setUARExist(false);
                    setOpenCreateModal(true);
                    setOpenSetupModal(false);
                }
            } catch (error) {
                if (error.response && (error.response.status === 401 || error.response.status === 404)) {
                    // Handle specific HTTP errors
                    setOpenCreateModal(true);
                    setOpenSetupModal(false);
                } else {
                    // Log other errors
                    console.error('Error fetching data:', error);
                    setError(error);
                }
            } finally {
                setIsLoading(false);
            }
        };

        // Add focus event listener to refresh data when returning to the page
        const handleFocus = () => {
            fetchData();
        };

        window.addEventListener('focus', handleFocus);
        fetchData(); // Initial fetch

        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [id, saved]);

    // Handle modals
    const handleCreateUAR = () => {
        setOpenCreateModal(true);
    };

    const handleCloseCreateModal = (event, reason) => {
        if (reason === 'backdropClick') {
            setOpenCreateModal(true);
        } else {
            setOpenCreateModal(false);
        }
    };

    const handleCloseSetupModal = (event, reason) => {
        if (reason === 'backdropClick') {
            setOpenSetupModal(true);
        } else {
            setOpenSetupModal(false);
        }
    };

    // Proceed to setup
    const createUAR = (e) => {
        e.preventDefault();
        setOpenCreateModal(false);
        setOpenSetupModal(true);
    };

    // Handle frequency selection
    const handleFrequencyChange = (selectedOptions) => {
        const frequency = selectedOptions.value;
        setSelectedFrequency(selectedOptions);

        const updatedData = {
            ...uarFile,
            APP_NAME: id,
            FREQUENCY: frequency
        };

        setUARFile(updatedData);
    };

    // Handle date selection
    const handleDateChange = (newValue) => {
        // Check if newValue is a valid Day.js object
        const formattedDate = newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : null;

        const updatedData = {
            ...uarFile,
            START_DATE: formattedDate
        };

        setUARFile(updatedData);
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Check if it's the START_DATE and format it accordingly
        let formattedValue = value;
        if (name === 'START_DATE' && value) {
            // If the field is START_DATE, treat it as a date and format it
            formattedValue = dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : null;
        }

        const updatedData = {
            ...uarFile,
            [name]: formattedValue
        };

        setUARFile(updatedData);
    };

    // Create UAR data records
    const createUARData = async (e) => {
        e.preventDefault();
        try {
            let startDate = new Date(uarFile.START_DATE);

            // Function to format a Date object to YYYY-MM-DD
            const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            // Function to add months to the start date based on the frequency
            const addMonthsToDate = (date, months) => {
                const newDate = new Date(date);
                newDate.setMonth(newDate.getMonth() + months);
                return newDate;
            };

            let recordsToCreate = [];

            // Based on frequency, determine how many records to create and assign REVIEW_TAG
            if (uarFile.FREQUENCY === 'Annually') {
                recordsToCreate.push({
                    ...uarFile,
                    START_DATE: formatDate(startDate),
                    REVIEW_TAG: 1 // Single record, tag it as 1
                });
            } else if (uarFile.FREQUENCY === 'Semi-Annually') {
                // Create two records, 6 months apart
                recordsToCreate.push({
                    ...uarFile,
                    START_DATE: formatDate(startDate),
                    REVIEW_TAG: 1
                });
                let secondRecordDate = addMonthsToDate(startDate, 6);
                recordsToCreate.push({
                    ...uarFile,
                    START_DATE: formatDate(secondRecordDate),
                    REVIEW_TAG: 2 // Second record gets tag 2
                });
            } else if (uarFile.FREQUENCY === 'Quarterly') {
                // Create 4 records, each 3 months apart
                for (let i = 0; i < 4; i++) {
                    let recordDate = addMonthsToDate(startDate, i * 3);
                    recordsToCreate.push({
                        ...uarFile,
                        START_DATE: formatDate(recordDate),
                        REVIEW_TAG: i + 1 // Tag from 1 to 4 based on index
                    });
                }
            } else if (uarFile.FREQUENCY === 'Monthly') {
                // Create 12 records, each 1 month apart
                for (let i = 0; i < 12; i++) {
                    let recordDate = addMonthsToDate(startDate, i);
                    recordsToCreate.push({
                        ...uarFile,
                        START_DATE: formatDate(recordDate),
                        REVIEW_TAG: i + 1 // Tag from 1 to 12 based on index
                    });
                }
            }

            // Now create the UAR records using the service for each calculated date
            for (let record of recordsToCreate) {
                const result = await uarService.createUAR(record);
                if (result) {
                    console.log('UAR record created successfully:', record.START_DATE, 'REVIEW_TAG:', record.REVIEW_TAG);
                }
            }

            setOpenSetupModal(false); // Close modal after completion
            setSaved(true);
            
            // Show success message
            setSnackbarMessage('User access review schedule created successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

        } catch (error) {
            console.error("There was a problem creating UAR file", error);
            setSnackbarMessage('Error creating access review schedule');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // View review details
    const handleViewReview = (uarID) => {
        navigate(`/useraccessreview/data/${id}/${uarID}`);
    };

    // Available frequency options
    const frequency = [
        { value: 'Monthly', label: 'Monthly' },
        { value: 'Quarterly', label: 'Quarterly' },
        { value: 'Semi-Annually', label: 'Semi-Annually' },
        { value: 'Annually', label: 'Annually' },
    ];

    // Prepare data for the table
    const rows = uar.map((record, index) => ({
        id: index + 1,
        FREQUENCY: record.FREQUENCY || '-',
        REVIEW_TAG: record.REVIEW_TAG || '-',
        START_DATE: record.START_DATE || '-',
        STATUS: record.STATUS || 'Pending',
        uarID: record.id,
    }));

    const getStatusChip = (status) => {
        switch(status) {
            case 'Completed':
                return (
                    <mui.Chip 
                        label="Completed" 
                        size="small" 
                        color="success" 
                        icon={<CheckCircleIcon />} 
                    />
                );
            case 'In Progress':
                return (
                    <mui.Chip 
                        label="In Progress" 
                        size="small" 
                        color="primary" 
                        icon={<HourglassEmptyIcon />} 
                    />
                );
            default:
                return (
                    <mui.Chip 
                        label="Pending" 
                        size="small" 
                        color="default" 
                        icon={<AccessTimeIcon />} 
                        variant="outlined"
                    />
                );
        }
    };

    // Table columns
    const columns = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'FREQUENCY', headerName: 'Frequency', flex: 1 },
        { field: 'REVIEW_TAG', headerName: 'Review Cycle', flex: 1 },
        { 
            field: 'START_DATE', 
            headerName: 'Start Date', 
            flex: 1,
            renderCell: (params) => (
                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarMonthIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                    {params.value}
                </mui.Box>
            )
        },
        { 
            field: 'STATUS', 
            headerName: 'Status', 
            flex: 1,
            renderCell: (params) => getStatusChip(params.value)
        },
    ];

    // Actions column
    const renderActionButton = (params) => {
        return (
            <mui.Button
                variant="outlined"
                size="small"
                startIcon={<FolderOpenIcon />}
                onClick={() => handleViewReview(params.row.uarID)}
                sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: 'none'
                }}
            >
                View Details
            </mui.Button>
        );
    };

    // Add action button to columns
    const columnsWithActions = [
        ...columns,
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            sortable: false,
            renderCell: renderActionButton,
        },
    ];

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                    <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/useraccessreview">
                            User Access Review
                        </mui.Link>
                        <mui.Typography color="text.primary">
                            {appInfo?.APP_NAME || 'Application Details'}
                        </mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 3 }}>
                        <RateReviewIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                        <mui.Box>
                            <mui.Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                {appInfo ? `${appInfo.APP_NAME} Access Reviews` : 'Application Access Reviews'}
                            </mui.Typography>
                            <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Manage and schedule user access reviews for this application
                            </mui.Typography>
                        </mui.Box>
                    </mui.Box>

                    <mui.Box sx={{ 
                        backgroundColor: '#f8fafc', 
                        borderRadius: 3,
                        minHeight: '80vh',
                        boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
                        p: 3
                    }}>
                        {isLoading ? (
                            <mui.Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <LogoLoader 
                                    size={80} 
                                    message="Loading Access Reviews..." 
                                    duration={1.5} 
                                />
                            </mui.Box>
                        ) : (
                            <>
                                {uarExist ? (
                                    <mui.Paper 
                                        elevation={0} 
                                        sx={{ 
                                            borderRadius: 2,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <mui.Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                                            <mui.Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                Scheduled Access Reviews
                                            </mui.Typography>
                                            <mui.Typography variant="body2" color="text.secondary">
                                                Review and manage your scheduled access reviews for {appInfo?.APP_NAME}
                                            </mui.Typography>
                                        </mui.Box>

                                        <Suspense fallback={
                                            <mui.Box sx={{ p: 4, textAlign: 'center' }}>
                                                <mui.CircularProgress size={40} />
                                                <mui.Typography sx={{ mt: 2 }}>Loading review data...</mui.Typography>
                                            </mui.Box>
                                        }>
                                            <DataTable
                                                rows={rows}
                                                columns={columns}
                                                columnsWithActions={columnsWithActions}
                                            />
                                        </Suspense>
                                    </mui.Paper>
                                ) : (
                                    <mui.Paper
                                        elevation={0}
                                        sx={{
                                            p: 4,
                                            textAlign: 'center',
                                            borderRadius: 2
                                        }}
                                    >
                                        <EditCalendarIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.7, mb: 2 }} />
                                        <mui.Typography variant="h6" sx={{ mb: 1 }}>
                                            No Access Reviews Scheduled
                                        </mui.Typography>
                                        <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            There are no user access reviews scheduled for this application yet.
                                        </mui.Typography>
                                        <mui.Button 
                                            variant="contained" 
                                            startIcon={<AddIcon />}
                                            onClick={handleCreateUAR}
                                        >
                                            Schedule Access Review
                                        </mui.Button>
                                    </mui.Paper>
                                )}
                            </>
                        )}
                    </mui.Box>

                    {/* Speed Dial */}
                    <CustomSpeedDial 
                        actions={[
                            { 
                                name: 'Schedule New Review', 
                                icon: <AddIcon />, 
                                onClick: handleCreateUAR 
                            }
                        ]} 
                    />

                    {/* Initial Setup Modal */}
                    <Modal
                        open={openCreateModal}
                        onClose={handleCloseCreateModal}
                        header="Access Review Setup"
                        body={
                            <>
                                <mui.Box sx={{ 
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    mb: 2
                                }}>
                                    <EditCalendarIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                                    <mui.Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                                        Setup Access Review Schedule
                                    </mui.Typography>
                                </mui.Box>
                                <mui.Typography variant="body1" sx={{ mb: 2 }}>
                                    No access review schedule has been set up for {appInfo?.APP_NAME}. Click the button below to start configuring your review schedule.
                                </mui.Typography>
                            </>
                        }
                        footer={
                            <>
                                <mui.Button 
                                    onClick={handleCloseCreateModal} 
                                    color="inherit"
                                >
                                    Cancel
                                </mui.Button>
                                <mui.Button 
                                    onClick={createUAR} 
                                    variant="contained" 
                                    startIcon={<SettingsIcon />}
                                >
                                    Configure Schedule
                                </mui.Button>
                            </>
                        }
                    />

                    {/* Schedule Configuration Modal */}
                    <Modal
                        size="sm"
                        open={openSetupModal}
                        onClose={handleCloseSetupModal}
                        header="Configure Access Review Schedule"
                        body={
                            <mui.Box sx={{ p: 1 }}>
                                <mui.Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 3 }}>
                                    Set up the review schedule for {appInfo?.APP_NAME}
                                </mui.Typography>
                                
                                <mui.FormControl fullWidth sx={{ mb: 3 }}>
                                    <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Review Frequency
                                    </mui.Typography>
                                    <MultipleSelect
                                        isMultiSelect={false}
                                        placeholderText="Select Frequency"
                                        selectedOptions={selectedFrequency}
                                        selectOptions={frequency}
                                        value={selectedFrequency}
                                        handleChange={handleFrequencyChange}
                                        styles={{
                                            menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 20000,
                                            }),
                                            control: (base) => ({
                                                ...base,
                                                zIndex: 10000,
                                            }),
                                        }}
                                        menuPortalTarget={document.body}
                                    />
                                </mui.FormControl>
                                
                                <mui.FormControl fullWidth sx={{ mb: 3 }}>
                                    <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        First Review Start Date
                                    </mui.Typography>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DemoContainer components={['DatePicker']}>
                                            <DatePicker
                                                value={isValidStartDate ? startDate : null}
                                                sx={{ width: '100%' }}
                                                onChange={handleDateChange}
                                                renderInput={(params) => (
                                                    <NormalTextField
                                                        {...params}
                                                        name="START_DATE"
                                                        sx={{ width: '100%' }}
                                                        onChange={handleInputChange}
                                                        onBlur={handleInputChange}
                                                    />
                                                )}
                                            />
                                        </DemoContainer>
                                    </LocalizationProvider>
                                </mui.FormControl>
                                
                                <mui.FormControl fullWidth sx={{ mb: 2 }}>
                                    <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Days to Complete Review
                                    </mui.Typography>
                                    <NormalTextField
                                        type="number"
                                        name="DAYS_TO_COMPLETE"
                                        value={uarFile.DAYS_TO_COMPLETE || ''}
                                        onChange={handleInputChange}
                                        fullWidth
                                        InputProps={{
                                            endAdornment: <mui.InputAdornment position="end">days</mui.InputAdornment>,
                                        }}
                                    />
                                </mui.FormControl>
                            </mui.Box>
                        }
                        footer={
                            <>
                                <mui.Button 
                                    onClick={handleCloseSetupModal} 
                                    color="inherit"
                                >
                                    Cancel
                                </mui.Button>
                                <mui.Button 
                                    onClick={createUARData} 
                                    variant="contained" 
                                    color="primary"
                                    disabled={!selectedFrequency || !isValidStartDate || !uarFile.DAYS_TO_COMPLETE}
                                >
                                    Create Schedule
                                </mui.Button>
                            </>
                        }
                    />

                    {/* Snackbar for notifications */}
                    <DynamicSnackbar
                        open={snackbarOpen}
                        message={snackbarMessage}
                        severity={snackbarSeverity}
                        onClose={handleSnackbarClose}
                    />
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    return (
        <ErrorBoundary fallback={<p>Failed to load data.</p>} error={error}>
            <Suspense fallback={
                <mui.Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '100vh',
                        backgroundColor: '#f8fafc'
                    }}
                >
                    <LogoLoader 
                        size={80} 
                        message="Loading Application Details..." 
                        duration={1.5} 
                    />
                </mui.Box>
            }>
                <div>
                    <SysOwnerSideBar mainContent={customMainContent} />
                </div>
            </Suspense>
        </ErrorBoundary>
    );
};

export default UserAccessReviewDetails;